package it.neckar.lizergy.model.configuration.moduleLayout.planning

import com.meistercharts.charts.pv.roofPlanning.ModuleAreas
import com.meistercharts.charts.pv.stringsPlanning.ModuleReference
import com.meistercharts.charts.pv.stringsPlanning.PvStringsPlanningModel
import com.meistercharts.charts.pv.stringsPlanning.RoofString
import com.meistercharts.charts.pv.stringsPlanning.StringIndex
import it.neckar.lizergy.model.configuration.moduleLayout.ModuleLayout.ModuleLayoutId
import it.neckar.lizergy.model.configuration.moduleLayout.ResolvedModuleLayout
import it.neckar.lizergy.model.configuration.moduleLayout.roof.ModulesString
import it.neckar.lizergy.model.configuration.moduleLayout.roof.ModulesString.StringId
import it.neckar.lizergy.model.configuration.quote.builder.ResolvedInverterConfiguration
import it.neckar.lizergy.model.configuration.quote.builder.previousInverterStringCount
import it.neckar.lizergy.model.configuration.quote.builder.toRestApiModel
import kotlinx.serialization.Serializable

/**
 * Serializable model for the roof planning model
 * This model contains exactly one plan for one roof.
 *
 * It can be converted from/to a [PvStringsPlanningModel] using methods defined in this class ([toRestApiModel] and [toMutableModel]/[applyToMutableModel])
 */
@Serializable
data class PvStringsPlanningModelInformation(val inverterConfigurations: List<ResolvedInverterConfiguration>) {
  /**
   * Creates a new planning model with the same settings
   */
  fun toMutableModel(forModuleLayout: ResolvedModuleLayout, moduleAreas: ModuleAreas): PvStringsPlanningModel {
    return PvStringsPlanningModel().also { targetModel ->
      applyToMutableModel(targetModel, forModuleLayout, moduleAreas)
    }
  }

  /**
   * Applies the settings from this module plan to the given ui model
   */
  fun applyToMutableModel(targetModel: PvStringsPlanningModel, forModuleLayout: ResolvedModuleLayout, moduleAreas: ModuleAreas) {
    targetModel.roofStringsConfiguration.set(inverterConfigurations.toRoofStringsConfiguration(forModuleLayout, moduleAreas))
  }
}

fun List<ResolvedInverterConfiguration>.toRoofStringsConfiguration(forModuleLayout: ResolvedModuleLayout, moduleAreas: ModuleAreas): List<RoofString> {
  return this.flatMapIndexed { inverterIndex, inverterConfiguration ->
    val previousInverterStringCount = this.previousInverterStringCount(inverterIndex)

    inverterConfiguration.relevantMpptConfigurations.flatMapIndexed { mpptInputIndex, mpptInputConfiguration ->
      val previousMpptInputStringCount = inverterConfiguration.previousMpptInputStringCount(mpptInputIndex)

      val relevantStringConfigurations = mpptInputConfiguration.stringConfigurations.filter {
        val optimalModuleCount = it.optimalModuleCount
        optimalModuleCount != null && optimalModuleCount > 0
      }
      relevantStringConfigurations.mapNotNull { it.modulesStrings }.flatMap { modulesStrings ->
        val stringIndex = relevantStringConfigurations.map { it.modulesStrings }.indexOf(modulesStrings)
        val allRoofStringsForModuleLayout = modulesStrings.filter { it.moduleLayoutId == forModuleLayout.id }
        val stringColorIndexOverride = StringIndex(previousInverterStringCount + previousMpptInputStringCount + stringIndex)
        allRoofStringsForModuleLayout.map { roofString ->
          val roofStringIndex = modulesStrings.indexOf(roofString)
          val previousModulesCount = modulesStrings.take(roofStringIndex).sumOf { it.numberOfModules }
          val followingModulesCount = modulesStrings.drop(roofStringIndex + 1).sumOf { it.numberOfModules }
          roofString.toMeisterChartsRoofString(inverterIndex, mpptInputIndex, StringIndex(stringIndex), stringColorIndexOverride, previousModulesCount, followingModulesCount, moduleAreas)
        }
      }
    }
  }.also {
    //Verify that there are not duplicate roof string ids
    it.forEach {
      println("Roof String: ${it.uuid} - Inverter Index: ${it.inverterIndex} - MPPT Input Index: ${it.mpptInputIndex} - String Index: ${it.stringIndex} - Color Index: ${it.globalStringIndex}")
    }
  }
}

fun ModulesString.toMeisterChartsRoofString(inverterIndex: Int, mpptInputIndex: Int, stringIndex: StringIndex, stringColorIndexOverride: StringIndex, previousModulesCount: Int, followingModulesCount: Int, moduleAreas: ModuleAreas): RoofString {
  val initialModules = this.stringOfModules.mapNotNull { moduleString ->
    moduleAreas.moduleAreas.firstOrNull { it.id == moduleString.moduleAreaId }
      ?.getModuleOrNull(moduleString.moduleIndex)
  }
  return RoofString(
    uuid = this.uuid,
    moduleLayoutUuid = this.moduleLayoutId.uuid,
    inverterIndex = inverterIndex,
    mpptInputIndex = mpptInputIndex,
    stringIndex = stringIndex,
    stringColorIndexOverride = stringColorIndexOverride,
    previousModulesCount = previousModulesCount,
    followingModulesCount = followingModulesCount,
    initialModules = initialModules,
  )
}

fun RoofString.toRestApiModel(): ModulesString {
  return ModulesString(
    id = StringId(this.uuid),
    moduleLayoutId = ModuleLayoutId(this.moduleLayoutUuid),
    stringOfModules = this.modules.map { module ->
      ModuleReference(
        moduleAreaId = module.modulePlacement.moduleArea.id,
        moduleIndex = module.modulePlacement.moduleIndex,
      )
    }
  )
}

/**
 * Converts the planning model to an immutable model that can be used for the REST API
 */
fun PvStringsPlanningModel.toRestApiModel(inverterConfigurations: List<ResolvedInverterConfiguration>): PvStringsPlanningModelInformation {
  return PvStringsPlanningModelInformation(
    inverterConfigurations = this.roofStringsConfiguration.toRestApiModel(inverterConfigurations),
  )
}
